/**
 * Country pin order (drag-and-drop) persisted in localStorage.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useCallback, useMemo, useState } from 'react';

const STORAGE_KEY = 'trafficpulse-country-pin-order';

function loadOrder(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? (parsed.filter((x) => typeof x === 'string') as string[]) : [];
  } catch {
    return [];
  }
}

function saveOrder(keys: string[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(keys));
  } catch {
    // ignore
  }
}

/**
 * Returns pinned countries in user-defined order (from localStorage).
 * Newly pinned countries appear at the end.
 */
export function useCountryPinOrder(pinnedCountries: { countryKey: string; countryName?: string }[]): {
  ordered: { countryKey: string; countryName?: string }[];
  setOrder: (countryKeys: string[]) => void;
  reorder: (from: number, to: number) => void;
} {
  const [savedOrder, setSavedOrder] = useState<string[]>(loadOrder);

  const ordered = useMemo(() => {
    const byKey = new Map(pinnedCountries.map((c) => [c.countryKey.toLowerCase(), c]));
    const keys = pinnedCountries.map((c) => c.countryKey.toLowerCase());

    const order = [...savedOrder];
    const seen = new Set(order.map((k) => k.toLowerCase()));

    for (const k of keys) {
      if (!seen.has(k)) {
        order.push(k);
        seen.add(k);
      }
    }

    return order.filter((k) => byKey.has(k)).map((k) => byKey.get(k)!);
  }, [pinnedCountries, savedOrder]);

  const setOrder = useCallback((countryKeys: string[]) => {
    const normalized = countryKeys.map((k) => k.trim().toLowerCase()).filter(Boolean);
    setSavedOrder(normalized);
    saveOrder(normalized);
  }, []);

  const reorder = useCallback(
    (from: number, to: number) => {
      const keys = ordered.map((c) => c.countryKey.toLowerCase());
      if (from < 0 || from >= keys.length || to < 0 || to >= keys.length) return;
      const [removed] = keys.splice(from, 1);
      keys.splice(to, 0, removed);
      setOrder(keys);
    },
    [ordered, setOrder],
  );

  return { ordered, setOrder, reorder };
}
